import React, { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertCircle, CalendarClock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEKLY_Q = [
  { key: "feeling", q: "How have you been feeling overall this week?", opts: [["very_good", "Very good"], ["good", "Good"], ["okay", "Okay"], ["not_well", "Not well"]] },
  { key: "adherence", q: "How well were you able to follow your meal plan this week?", hint: "1 = Not at all, 5 = Very consistently", scale: true },
  { key: "biggest_challenge", q: "What was your biggest challenge this week?", opts: [["meal_prep_time", "Finding time to prepare meals"], ["food_cost", "Food cost or availability"], ["hunger_cravings", "Hunger or cravings"], ["eating_out", "Eating outside the home"], ["work_family", "Work or family commitments"], ["appetite_symptoms", "Appetite or symptoms"], ["medication_concerns", "Medication concerns"], ["no_major", "No major challenges"], ["other", "Something else"]] },
  { key: "symptoms_status", q: "Have you experienced any new symptoms or significant changes in your health?", opts: [["no", "No"], ["yes", "Yes"], ["discuss", "I'd prefer to discuss this with my dietitian"]] },
  { key: "support_needed", q: "What support would help you most right now?", optional: true, opts: [["meal_plan_advice", "Advice on my meal plan"], ["portions_choices", "Help with portions and food choices"], ["consistency", "Help with staying consistent"], ["progress_measurements", "Discuss my progress and health measurements"], ["none", "I'm doing well and don't need additional support"]] },
];

const REVIEW_Q = [
  { key: "experience_rating", q: "Overall, how would you rate your experience with PlateMind?", hint: "1 = Very poor, 5 = Excellent", scale: true },
  { key: "progress_feeling", q: "How do you feel about your progress over the 12 weeks?", opts: [["significant", "I've made significant progress"], ["some", "I've made some progress"], ["little", "I've made little or no progress"], ["unsure", "I'm not sure yet"]] },
  { key: "most_helpful", q: "What has been most helpful during the programme?", text: true, optional: true },
  { key: "most_difficult", q: "What has been the most difficult part, or what could we improve?", text: true, optional: true },
  { key: "next_step", q: "What would you like to do next?", opts: [["complete", "Complete my programme for now"], ["continue", "Continue with PlateMind's nutrition support"], ["book_session", "Book a session with my dietitian before deciding"]] },
  { key: "anything_else", q: "Is there anything else you would like your dietitian to know before your programme ends?", text: true, optional: true },
];

const Shell = ({ children, testid }) => (
  <div className="min-h-screen bg-[#FAFAF9] py-8 px-4" data-testid={testid}>
    <div className="max-w-xl mx-auto space-y-5">
      <img src="/platemind-logo-new.png" alt="PlateMind" className="h-9 w-auto" />
      {children}
    </div>
  </div>
);

const Question = ({ item, index, value, onChange }) => (
  <Card className="rounded-2xl border-stone-200 p-5 space-y-3" data-testid={`question-${item.key}`}>
    <p className="text-sm font-semibold text-stone-900"><span className="text-[#FF7A00] mr-1.5">{index + 1}.</span>{item.q}{item.optional && <span className="text-stone-400 font-normal"> (optional)</span>}</p>
    {item.hint && <p className="text-xs text-stone-400 -mt-1">{item.hint}</p>}
    {item.scale && (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map(n => (
          <button key={n} type="button" onClick={() => onChange(n)} data-testid={`${item.key}-${n}`}
            className={`flex-1 h-11 rounded-xl text-sm font-bold transition-colors ${value === n ? "bg-[#FF7A00] text-white" : "bg-white border border-stone-200 text-stone-700 hover:border-[#FF7A00]"}`}>{n}</button>
        ))}
      </div>
    )}
    {item.opts && (
      <div className="grid gap-2">
        {item.opts.map(([v, label]) => (
          <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${item.key}-${v}`}
            className={`text-left px-4 py-2.5 rounded-xl text-sm transition-colors ${value === v ? "bg-[#FFF4EA] border-2 border-[#FF7A00] text-stone-900 font-medium" : "bg-white border border-stone-200 text-stone-700 hover:border-[#FF7A00]/50"}`}>{label}</button>
        ))}
      </div>
    )}
    {item.text && <Textarea value={value || ""} onChange={e => onChange(e.target.value)} rows={3} className="rounded-xl" data-testid={`${item.key}-input`} />}
  </Card>
);

const BookingCta = ({ url, label = "Book a dietitian session" }) => url ? (
  <a href={url} className="block"><Button variant="outline" className="w-full rounded-xl h-12 border-[#FF7A00]/40 text-[#FF7A00] hover:bg-[#FFF4EA]" data-testid="book-followup-btn"><CalendarClock className="w-4 h-4 mr-2" />{label}</Button></a>
) : null;

const RenewalPanel = ({ token, options, confirmed, status }) => {
  const [done, setDone] = useState(confirmed);
  const [busy, setBusy] = useState(false);
  const call = async (path) => {
    const res = await fetch(`${API}/checkin/${token}/${path}`, { method: "POST" });
    const d = await res.json();
    if (!res.ok) throw new Error(d.detail || "Something went wrong.");
    return d;
  };
  const pay = async () => {
    setBusy(true);
    try {
      if (!done) { await call("continue-confirm"); setDone(true); }
      const d = await call("renewal-payment");
      if (d.already_renewed) { toast.success(d.message); return; }
      window.location.href = d.authorization_url;
    } catch (e) { toast.error(e.message); setBusy(false); }
  };
  if (!options) return null;
  if (status === "renewed") return <p className="text-sm text-green-700 font-medium" data-testid="renewal-active">Your renewal is active. Your dietitian will be in touch about your next phase.</p>;
  return (
    <div className="text-left bg-stone-50 rounded-xl p-4 space-y-2" data-testid="renewal-options">
      <p className="text-sm font-bold text-stone-900">Continue with PlateMind: {options.duration_weeks} weeks</p>
      {options.price > 0 && <p className="text-lg font-bold text-stone-900" data-testid="renewal-price">{options.currency} {Number(options.price).toLocaleString()}</p>}
      <ul className="text-xs text-stone-500 list-disc pl-4">{(options.inclusions || []).map(i => <li key={i}>{i}</li>)}</ul>
      {done && <p className="text-xs text-green-700" data-testid="continue-confirmed">You've confirmed you want to continue.</p>}
      <Button onClick={pay} disabled={busy || !(options.price > 0)} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-11" data-testid="confirm-continue-btn">
        {busy && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}{done ? "Pay securely to continue" : "Yes, continue and pay securely"}
      </Button>
      <p className="text-[11px] text-stone-400">You'll be taken to Paystack to pay. Your renewal starts only after the payment is confirmed.</p>
    </div>
  );
};

const RenewalVerify = ({ token, reference }) => {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    fetch(`${API}/checkin/${token}/renewal-verify?reference=${encodeURIComponent(reference)}`)
      .then(async r => { const d = await r.json(); setState({ ok: r.ok && ["success", "already_verified"].includes(d.status), msg: d.message || d.detail }); })
      .catch(() => setState({ ok: false, msg: "We couldn't confirm your payment yet." }));
  }, [token, reference]);
  return (
    <Shell testid="renewal-verify">
      <Card className="rounded-2xl p-8 text-center space-y-3">
        {state.loading ? <Loader2 className="w-8 h-8 animate-spin text-[#FF7A00] mx-auto" /> : state.ok
          ? <><CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" /><h1 className="text-xl font-bold text-stone-900" data-testid="renewal-success">Payment confirmed</h1><p className="text-sm text-stone-500">Your programme has been renewed. Your dietitian will be in touch about your next phase.</p></>
          : <><AlertCircle className="w-12 h-12 text-amber-500 mx-auto" /><h1 className="text-xl font-bold text-stone-900" data-testid="renewal-pending">Payment not confirmed yet</h1><p className="text-sm text-stone-500">{state.msg} If you were charged, contact hello@platemind.app and we'll sort it out.</p></>}
      </Card>
    </Shell>
  );
};

const CheckInForm = () => {
  const { token } = useParams();
  const [params] = useSearchParams();
  const payRef = params.get("renewal") === "verify" ? (params.get("reference") || params.get("trxref")) : null;
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  const [answers, setAnswers] = useState({});
  const [saving, setSaving] = useState(false);
  const preview = params.get("preview") === "1";

  useEffect(() => {
    fetch(`${API}/checkin/${token}`).then(async r => {
      if (!r.ok) throw new Error();
      const d = await r.json();
      setInfo(d);
      if (d.status === "already_completed" && params.get("preview") !== "1") setResult({ already: true, next_step: d.next_step, renewal_options: d.renewal_options, continuation_confirmed: d.continuation_confirmed, continuation_status: d.continuation_status });
    }).catch(() => setError("This check-in link is not valid or has expired."));
  }, [token]);

  if (payRef) return <RenewalVerify token={token} reference={payRef} />;
  if (error) return <Shell testid="checkin-error"><Card className="rounded-2xl p-8 text-center"><AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-3" /><p className="text-sm text-stone-600">{error}</p></Card></Shell>;
  if (!info) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#FF7A00]" /></div>;

  const isReview = info.checkin_type === "programme_review";
  const questions = isReview ? REVIEW_Q : WEEKLY_Q;

  const submit = async () => {
    if (preview) { toast.info("Preview only. Answers are not saved."); return; }
    const missing = questions.find(q => !q.optional && (answers[q.key] === undefined || answers[q.key] === ""));
    if (missing) { toast.error(`Please answer: ${missing.q}`); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/checkin/${token}`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(answers) });
      const d = await res.json();
      if (res.ok) setResult(d); else toast.error(d.detail || "Submission failed.");
    } catch { toast.error("Connection error."); } finally { setSaving(false); }
  };

  if (result) return (
    <Shell testid="checkin-success">
      <Card className="rounded-2xl p-8 text-center space-y-4">
        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
        <h1 className="text-xl font-bold text-stone-900">{isReview ? "Programme review received" : "Check-in complete"}</h1>
        <p className="text-sm text-stone-500">{result.already ? "You have already submitted this. Thank you." : result.message}</p>
        {isReview && result.next_step === "continue" && <RenewalPanel token={token} options={result.renewal_options} confirmed={result.continuation_confirmed} status={result.continuation_status} />}
        {isReview && result.next_step === "book_session" && <BookingCta url={result.booking_info?.booking_url || info.booking_link} label="Book my Week 12 session" />}
        {!isReview && <BookingCta url={result.booking_info?.booking_url || info.booking_link} label="Book a dietitian session (optional)" />}
      </Card>
    </Shell>
  );

  return (
    <Shell testid="checkin-form-page">
      {preview && <div className="rounded-xl bg-blue-50 border border-blue-200 text-blue-800 text-sm px-4 py-3" data-testid="checkin-preview-banner">Staff preview: this is what the patient sees. Nothing you enter here is saved.</div>}
      <div className="pt-2">
        <p className="text-xs font-semibold text-[#FF7A00] uppercase tracking-widest">{info.programme_week}</p>
        <h1 className="text-2xl sm:text-3xl font-bold text-stone-900 mt-1">{isReview ? "Your 12-week programme review" : "Weekly check-in"}</h1>
        <p className="text-sm text-stone-500 mt-2">
          {isReview ? `Hello ${info.patient_name}, thank you for being part of this journey. Your answers help your dietitian support your next steps. Optional questions can be skipped.`
            : `Hello ${info.patient_name}, this takes about 2 minutes. Booking a session is optional.`}
        </p>
      </div>
      {questions.map((q, i) => <Question key={q.key} item={q} index={i} value={answers[q.key]} onChange={v => setAnswers(a => ({ ...a, [q.key]: v }))} />)}
      {!isReview && answers.biggest_challenge === "other" && (
        <Textarea placeholder="Tell us a bit more (optional)" value={answers.challenge_other || ""} onChange={e => setAnswers(a => ({ ...a, challenge_other: e.target.value }))} className="rounded-xl" data-testid="challenge-other-input" />
      )}
      {!isReview && (
        <Textarea placeholder="Anything else you'd like to add? (optional)" value={answers.notes || ""} onChange={e => setAnswers(a => ({ ...a, notes: e.target.value }))} className="rounded-xl" data-testid="notes-input" />
      )}
      <Button onClick={submit} disabled={saving} className="w-full bg-[#FF7A00] hover:bg-[#E56E00] text-white rounded-xl h-12 text-base font-bold" data-testid="submit-checkin">
        {saving ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <ArrowRight className="w-5 h-5 mr-2" />}{isReview ? "Submit programme review" : "Submit check-in"}
      </Button>
      {!isReview && <BookingCta url={info.booking_link} />}
      <p className="text-[11px] text-stone-400 text-center">If you feel unwell or have urgent symptoms, contact your doctor or visit your nearest hospital.</p>
    </Shell>
  );
};

export default CheckInForm;
